import { isDefined } from '@follow/cdk'
import { FunctionComponent, useCallback, useEffect } from 'react'
import { ORDOCLIC_APP_EPRESCRIPTION_URL } from '../../../environment/ordoclic'
import { useDictationContext } from '../../../core/provider/dictation-context.provider'
import {
  isMultiPrescriptionsMessage,
  OrdoclicApiLightBatchedPrescription,
  OrdoclicApiLightMessage,
  OrdoclicApiResponse,
} from '../../../model/ordoclic'
import { IconButton, Modal } from '../../shared'
import { OrdoclicIframeProps, PrescriptionFilesParams } from './OrdoclicIframe.model'
import styles from './OrdoclicIframe.module.scss'

const getPrescriptionIds = (ordoclicBatch: OrdoclicApiLightBatchedPrescription[]) =>
  ordoclicBatch.reduce((acc: string[], value) => {
    if (value.isPrescription) {
      acc.push(value.id)
    }
    return acc
  }, [])

const createMultiPatientsPrescriptionFilesParams = (
  patientPrescriptions: OrdoclicApiLightMessage[],
) =>
  patientPrescriptions.reduce((acc: PrescriptionFilesParams, value) => {
    const existingPatientPrescriptionsIndex = acc.findIndex(
      (patientPrescription) => patientPrescription.ordoclicPatientId === value.patientId,
    )

    if (existingPatientPrescriptionsIndex !== -1) {
      const existingPatientPrescriptionsUpdated = {
        ordoclicPatientId: acc[existingPatientPrescriptionsIndex].ordoclicPatientId,
        prescriptionIds: [
          ...acc[existingPatientPrescriptionsIndex].prescriptionIds,
          ...getPrescriptionIds(value.batch),
        ],
      }

      acc[existingPatientPrescriptionsIndex] = existingPatientPrescriptionsUpdated
    } else {
      const newPatientPrescription = {
        ordoclicPatientId: value.patientId,
        prescriptionIds: getPrescriptionIds(value.batch),
      }
      acc.push(newPatientPrescription)
    }
    return acc
  }, [])

export const OrdoclicIframe: FunctionComponent<OrdoclicIframeProps> = ({
  generatePrescriptionFiles,
  onClose,
  isUnmounting,
  onSelectPatient,
  url,
  title,
}) => {
  const { hideWidget, showWidget } = useDictationContext()

  useEffect(() => {
    hideWidget()
    return () => showWidget()
  }, [hideWidget, showWidget])

  const ordoclicMessageHandler = useCallback(
    (event: MessageEvent) => {
      if (event.origin === ORDOCLIC_APP_EPRESCRIPTION_URL && isDefined(event.data)) {
        const ordoclicMessage = event.data as OrdoclicApiResponse
        switch (ordoclicMessage.type) {
          case 'all-batch-created':
            if (isMultiPrescriptionsMessage(ordoclicMessage.message)) {
              const multiPatientsPrescriptionFilesParams =
                createMultiPatientsPrescriptionFilesParams(ordoclicMessage.message)
              generatePrescriptionFiles(multiPatientsPrescriptionFilesParams, 'multi')
              ordoclicMessage.message.forEach((prescription) =>
                onSelectPatient(prescription.patientId),
              )
            }
            break
          case 'batch-created':
            if (!isMultiPrescriptionsMessage(ordoclicMessage.message)) {
              const ordoclicPatientId = ordoclicMessage.message.patientId
              const prescriptionIds = getPrescriptionIds(ordoclicMessage.message.batch)
              const uniquePatientPrescriptionFilesParams = [{ ordoclicPatientId, prescriptionIds }]
              generatePrescriptionFiles(uniquePatientPrescriptionFilesParams, 'unique')
              onSelectPatient(ordoclicPatientId)
            }
            break
          case 'patient-selected':
            if (!isMultiPrescriptionsMessage(ordoclicMessage.message)) {
              const ordoclicPatientId = ordoclicMessage.message.patientId
              if (ordoclicPatientId !== null) {
                onSelectPatient(ordoclicPatientId)
              }
            }
            break
          case 'close-iframe':
            onClose()
            break
        }
      }
    },
    [generatePrescriptionFiles, onClose, onSelectPatient],
  )

  useEffect(() => {
    window.addEventListener('message', ordoclicMessageHandler)
    return () => {
      window.removeEventListener('message', ordoclicMessageHandler)
    }
  }, [ordoclicMessageHandler])

  return (
    <Modal isUnmounting={isUnmounting} onClickOutside={onClose}>
      <div className={styles.container}>
        <div className={styles.backWrapper}>
          <IconButton
            onClick={onClose}
            icon="back"
            theme="light"
            testId="button-exit-ordoclic-iframe"
          />
          <span className={styles.backReturnText}>{title}</span>
        </div>
        <iframe
          className={styles.iframe}
          title="ordoclic-iframe"
          allow="camera; loopback-network *; local-network *; local-network-access *"
          src={url}
        />
      </div>
    </Modal>
  )
}
